import React, { memo } from 'react'
import type { ShopifyHit } from '../types'
import {
  ShopGridItem,
  ShopProductImage,
  ShopProductInfo,
  ShopProductTitle,
  ShopProductPrice,
  ShopProductAction,
} from '../styles'
import { HighlightedText } from './HighlightedText'
import { formatPrice } from '../../../Utils/formatting/formatting'

type ShopResultItemProps = {
  item: ShopifyHit
  query: string
  onSelect: () => void
  isGridView?: boolean
}

export const ShopResultItem = memo<ShopResultItemProps>(
  ({ item, query, onSelect, isGridView = false }) => {
    const handleKeyDown = (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelect()
      }
    }

    const priceText = formatPrice(item.price)

    return (
      <ShopGridItem
        role="option"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
        aria-label={`${item.title}${priceText ? `, ${priceText}` : ''}`}
        $isGridView={isGridView}
      >
        {item.image && (
          <ShopProductImage
            src={item.image}
            alt={item.title || 'Shop featured image'}
            loading="lazy"
            $isGridView={isGridView}
          />
        )}
        <ShopProductInfo>
          <ShopProductTitle $isGridView={isGridView}>
            <HighlightedText text={item.title || ''} query={query} />
          </ShopProductTitle>
          {priceText && <ShopProductPrice>{priceText}</ShopProductPrice>}
        </ShopProductInfo>
        <ShopProductAction $isGridView={isGridView}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </ShopProductAction>
      </ShopGridItem>
    )
  }
)

ShopResultItem.displayName = 'ShopResultItem'
